const modifiers = new WeakMap();

/**
 * Any modifier that adds classes to an element, where that element also has a
 * class attribute value of a template expression, should register itself with
 * `classChangingModifier`, to update modifier related classes whenever the
 * element's class or component imported class from `...attributes` changes.
 * @example
 *     modify(el) {
 *       classChangingModifier(el, this.updateClasses);
 */
export function registerClassModifier(el, updateFn) {
  let config = modifiers.get(el);
  if (!config) {
    config = {
      className: el.className,
      observer: new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'class') {
            const el = mutation.target;
            const { className } = el;
            const config = modifiers.get(el);
            if (className !== config.className) {
              config.className = className;
              config.updateFn();
            }
          }
        }
      }),
    };
    config.observer.observe(el, { attributes: true });
  }
  config.updateFn = updateFn;
  modifiers.set(el, config);
}

export function destroyClassModifier(el) {
  const config = modifiers.get(el);
  if (!config) {
    return;
  }
  config.observer.disconnect();
  modifiers.delete(el);
}

/**
 * Called with a modifier registered with `classChangingModifier`, after
 * modifier related class changes, will update cached class value for modifier's
 * element, to avoid repeated calls to registered callback as a result of new
 * class attribute mutations.
 */
export function updateClassModifier(el) {
  const config = modifiers.get(el);
  if (config) {
    config.className = el.className;
  }
}
